import { Link as RouterLink } from 'react-router-dom';
import { Box, Text, Heading } from '@primer/react';
import Reveal from '../components/Reveal';
import SEO from '../components/SEO';
import PageHero from '../components/PageHero';
import UXAuditCallout from '../components/UXAuditCallout';
import ServiceCTA from '../components/ServiceCTA';
import FAQ from '../components/FAQ';
import { serviceDetails, serviceOrder } from '../data/serviceDetails';
import { breadcrumbSchema, faqSchema, SITE_URL } from '../utils/seo';

const categories = serviceOrder.map((slug) => serviceDetails[slug]);

const faqs = [
  {
    question: 'Is Pegasus UX a Dallas-based UX design agency?',
    answer:
      "Yes. Pegasus UX is headquartered in Dallas, TX. We work with businesses across the Dallas-Fort Worth area as well as enterprise, fintech, and SaaS teams nationwide.",
  },
  {
    question: 'What makes Pegasus UX one of the best UX design agencies?',
    answer:
      "We combine research-backed process with deep experience across enterprise, fintech, AI, logistics, and SaaS products, the kind of range most UI/UX design firms don't have. Our clients include GitHub, McKinsey, Verizon, and State Farm.",
  },
  {
    question: 'Do you offer UX audits?',
    answer:
      'Yes. Our UX audit company services include a heuristic and UX audit, accessibility audit, competitive benchmarking, and a prioritized findings report, delivered fast with no long-term commitment required.',
  },
  {
    question: 'Do you provide UX design services for SaaS companies?',
    answer:
      'Yes, SaaS UX design is one of our core specialties. We help SaaS teams turn complex workflows into interfaces users actually enjoy.',
  },
  {
    question: 'Can you help with UX for enterprise applications?',
    answer:
      "Absolutely. Enterprise UX is one of the most technically demanding disciplines in the industry, legacy systems, dozens of stakeholders, workflows that can't break. It's exactly where we specialize.",
  },
  {
    question: 'Do you specialize in fintech UX design?',
    answer:
      "Yes. We've designed fintech UI/UX for platforms handling real money and real compliance requirements, including State Farm's financial technology platforms.",
  },
  {
    question: 'Do you offer AI UX design services?',
    answer:
      "Yes. We partnered with GitHub's Enterprise and Customer Success teams to help incorporate Copilot AI into their product, and we bring that same AI UX design expertise to our clients.",
  },
  {
    question: "What's included in your UX testing services?",
    answer:
      'Our UX testing services include usability testing, journey mapping, and analytics-backed insights, so every design decision is backed by evidence, not opinion.',
  },
];

const schema = [
  breadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
  ]),
  faqSchema(faqs),
];

function Services() {
  return (
    <Box sx={{ backgroundColor: 'canvas.default' }}>
      <SEO
        title="UX Design Services in Dallas | Audits, Design, Strategy & Research"
        description="Explore Pegasus UX's UI/UX design services from our Dallas, TX studio: UX audits, UX design, UX strategy, and UX research built for enterprise, fintech, AI, logistics, and SaaS companies."
        keywords="ux design agency dallas tx, dallas ux consulting services, ui ux design services company, ux consulting companies, best ux design agencies, ui ux design firm, ux audit company, ux testing services, UX design services, UX strategy consulting, UX research services, fintech UX design, saas ux design, enterprise ux, logistics ux design"
        path="/services"
        schema={schema}
        image={`${SITE_URL}/og/services.jpg`}
      />
      <PageHero
        title="UX Research, Design & Strategy Services"
        subtitle="Pegasus UX is a dynamic, full-service UX agency built for complex products. We partner with enterprise, fintech, AI, developer tooling, logistics, and SaaS teams to design experiences that make complicated workflows feel effortless. We value research and rigor as much as craft, and bring both to every engagement."
      />

      <Box
        sx={{
          paddingTop: [6, 7, 8],
          paddingBottom: [7, 8, 9],
          paddingX: ['24px', '48px', '100px'],
        }}
      >
        <Box sx={{ maxWidth: '1400px', margin: '0 auto' }}>
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: ['1fr', '1fr', '1fr', 'repeat(3, 1fr)'],
            gap: [6, 6, 6, 5],
          }}
        >
          {categories.map((category, index) => (
            <Reveal
              key={category.slug}
              delay={index * 0.12}
              sx={{ textAlign: ['center', 'center', 'center', 'left'] }}
            >
              <Heading
                as="h2"
                sx={{
                  margin: 0,
                  marginBottom: 4,
                  fontFamily: "'Fraunces', serif",
                  fontWeight: 300,
                  fontSize: ['2.5rem', '3rem', '3.5rem'],
                  color: 'fg.default',
                }}
              >
                {category.title}
              </Heading>

              <Box
                as={RouterLink}
                to={`/services/${category.slug}`}
                sx={{
                  display: 'inline-block',
                  marginBottom: 5,
                  paddingX: 4,
                  paddingY: 2,
                  borderRadius: '6px',
                  border: '1px solid rgba(27, 31, 36, 0.15)',
                  backgroundColor: '#1f883d',
                  color: '#ffffff',
                  fontSize: 0,
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  ':hover': { backgroundColor: '#1c8139' },
                  ':active': { backgroundColor: '#197935' },
                }}
              >
                View Overview
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: ['center', 'center', 'center', 'flex-start'],
                  gap: 3,
                }}
              >
                {category.services.map((service) => (
                  <Text
                    key={service}
                    as="p"
                    sx={{
                      margin: 0,
                      fontSize: 0,
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: 'fg.muted',
                    }}
                  >
                    {service}
                  </Text>
                ))}
              </Box>
            </Reveal>
          ))}
        </Box>
        </Box>
      </Box>

      <UXAuditCallout />

      <FAQ
        eyebrow="FAQ"
        title="Common questions from enterprise, fintech, AI & SaaS teams."
        items={faqs}
      />

      <ServiceCTA
        eyebrow="Stop assuming. Start knowing."
        headline={[
          { text: "Let's find out what your", tone: 'plain' },
          { text: 'users actually need.', tone: 'gradient' },
        ]}
        subtitle="Schedule a consultation and let's talk about how Pegasus UX can help your team ship a product people love."
      />
    </Box>
  );
}

export default Services;
